import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notifyNativeLogin, notifyNativeLogout } from '../lib/nativeBridge';

const AuthContext = createContext(null);

// Same environment detection as api.js
const getBaseURL = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const API_URL = getBaseURL();

// Configure axios defaults
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      notifyNativeLogin(response.data.company_id);
    } catch (error) {
      // Try to refresh token
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(response.data);
        notifyNativeLogin(response.data.company_id);
      } catch (refreshError) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    notifyNativeLogin(response.data.company_id);
    return response.data;
  };

  const register = async (email, password, name, role = 'technician') => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name, role },
      { withCredentials: true }
    );
    setUser(response.data);
    notifyNativeLogin(response.data.company_id);
    return response.data;
  };

  // Self-service trial: creates the company + first admin user and logs in,
  // all in one call. No payment involved — upgrading happens later via
  // in-app purchase (see UpgradePage / nativeBridge).
  const signupTrial = async ({ companyName, adminName, email, password, phone }) => {
    const response = await axios.post(`${API_URL}/api/public/signup-trial`,
      { company_name: companyName, admin_name: adminName, email, password, phone },
      { withCredentials: true }
    );
    setUser(response.data);
    notifyNativeLogin(response.data.company_id);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    notifyNativeLogout();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, signupTrial, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
